import { useState, useEffect } from "react";
import { usePageWrapper } from "../../../../hooks/page-wrapper.hook";
import {
  Typography,
  IconButton,
  Grid2,
  CircularProgress,
  Button,
  Slider,
  Chip,
  Divider,
} from "@mui/material";
import { useApiService, ArtDialog, ArtButton, ArtTextField } from "@art/shared-ui";
import EmailRulesList from "./email-rule";
import AddIcon from "@mui/icons-material/Add";
import { faAdd } from "@fortawesome/free-solid-svg-icons";

const AppUsageAlert = () => {
  const [threshold, setThreshold] = useState("");
  const [email, setEmail] = useState("");
  const [emailList, setEmailList] = useState<string[]>([]);
  const [rules, setRules] = useState<any[]>([]);
  const [editMode, setEditMode] = useState(false);
  const [editRuleId, setEditRuleId] = useState<string | null>(null);
  const [originalThreshold, setOriginalThreshold] = useState("");
  const [originalEmailList, setOriginalEmailList] = useState<string[]>([]);
  const apiService = useApiService();
  const [isLoading, setIsLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const fetchUsageAlerts = async () => {
    try {
      const response = await apiService.get(`/organisation/usage-alert`);
      if (response.status === 200) {
        setRules(response?.data?.data || []);
      }
    } catch (error: any) {
      console.error("Error: failed to fetch the usage alerts");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUsageAlerts();
  }, []);

  const handleAddEmail = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!email) {
      apiService.showSnack("Please enter an email address", "error");
      return;
    }

    if (!emailRegex.test(email)) {
      apiService.showSnack("Invalid email format", "error");
      return;
    }

    if (emailList.includes(email)) {
      apiService.showSnack("Email already added", "error");
      return;
    }

    setEmailList([...emailList, email]);
    setEmail("");
  };

  const handleRemoveEmail = (emailToRemove: string) => {
    setEmailList(emailList.filter((obj: string) => obj !== emailToRemove));
  };

  const handleAddRule = async () => {
    const percentage = Number.parseInt(threshold, 10);
    if (!Number.isFinite(percentage) || percentage <= 0 || percentage > 100) {
      apiService.showSnack("Enter valid threshold between 1-100%", "error");
      return;
    }
    if (emailList.length <= 0) {
      apiService.showSnack("Enter the email address", "error");
      return;
    }

    if (!editMode) {
      if (rules.some((rule: any) => rule.gt_percentage === percentage)) {
        apiService.showSnack("Duplicate rule exists", "error");
        return;
      }
    }

    try {
      const payload = {
        id: editMode ? editRuleId : undefined,
        gt_percentage: percentage,
        emails: emailList,
      };
      const response = await apiService.post(`/organisation/usage-alert`, payload);

      if (response?.data) {
        apiService.showSnack(
          `Usage alert ${editMode ? "updated" : "added"} successfully!`,
          "success"
        );
        fetchUsageAlerts();
        setDrawerOpen(false);
        handleReset();
      }
    } catch (error: any) {
      apiService.showSnack(
        error?.response?.data?.message ||
        `Failed to ${editMode ? "update" : "add"} usage alert`,
        "error"
      );
    }
  };

  const handleRemoveRule = async (id: string) => {
    try {
      await apiService.delete(`/organisation/usage-alert`, {
        data: { id: id },
      });

      setRules(rules.filter((rule: any) => rule._id !== id));
      apiService.showSnack("Usage alert deleted successfully!", "success");
      fetchUsageAlerts();
    } catch (error: any) {
      apiService.showSnack(
        error?.response?.data?.message || "Failed to delete usage alert",
        "error"
      );
    }
  };

  const handleEditRule = (rule: any) => {
    setEditMode(true);
    setEditRuleId(rule._id);
    setOriginalThreshold(rule.gt_percentage.toString());
    setOriginalEmailList(rule.emails);
    setThreshold(rule.gt_percentage.toString());
    setEmailList(rule.emails);
    setDrawerOpen(true);
  };

  const handleReset = () => {
    if (editMode) {
      setThreshold(originalThreshold);
      setEmailList(originalEmailList);
    } else {
      setThreshold("");
      setEmailList([]);
    }
    setEmail("");
    setEditMode(false);
    setEditRuleId(null);
  };

  const handleClose = () => {
    setDrawerOpen(false);
    setEditMode(false);
    setEditRuleId(null);
    setOriginalEmailList([]);
    setThreshold("");
    setEmailList([]);
  };

  return (
    <div>
      <div className="analytics-header mb-3">
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-4">
          <Typography variant="h3" className="art_dashboard_title mb-2">
            Usage Notification
          </Typography>
          <ArtButton
            label="Create alert"
            size="medium"
            onClick={() => setDrawerOpen(true)}
            icon={faAdd}
          />
        </div>

        <Typography variant="body2" className="mb-4">
          Monitor and manage your connection usage effectively with automated
          alerts. Receive email notifications when usage reaches predefined
          thresholds. Set up custom rules to track usage percentages and ensure
          seamless operation without exceeding limits.
        </Typography>
      </div>
      <Grid2
        container
        spacing={2}
        flexDirection={{
          xs: "column-reverse",
          sm: "column-reverse",
          md: "row",
        }}
      >
        <Grid2 size={{ xs: 12, sm: 12, md: 12, xl: 12 }}>
          {isLoading ? (
            <div className="d-flex align-items-center justify-content-center py-4">
              <CircularProgress />
            </div>
          ) : rules?.length === 0 ? (
            <div>
              <Grid2
                container
                alignItems="center"
                justifyContent="center"
                spacing={4}
              >
                <Grid2
                  size={{ xs: 12, md: 6 }}
                  sx={{ textAlign: { xs: "center" } }}
                >
                  <img
                    src="/assets/images/push-notifications.svg"
                    alt="No alerts"
                    style={{ maxWidth: "380px", width: "100%" }}
                  />
                </Grid2>

                <Grid2 size={{ xs: 12, md: 6 }}>
                  <Typography variant="h3" fontWeight={600} gutterBottom>
                    No usage alerts yet
                  </Typography>

                  <Typography variant="body2">
                    Stay informed before limits are reached.
                  </Typography>

                  <Typography variant="body2">
                    Create alerts to notify your team when usage crosses key
                    thresholds.
                  </Typography>
                </Grid2>
              </Grid2>
            </div>
          ) : (
            <EmailRulesList
              rules={rules}
              removeRule={handleRemoveRule}
              editRule={handleEditRule}
            />
          )}
        </Grid2>
      </Grid2>

      <ArtDialog
        title={editMode ? "Edit Alert Rule" : "Create Alert Rule"}
        open={drawerOpen}
        onClose={handleClose}
        size="medium"
        actionBuilder={() => (
          <div className="d-flex align-items-center justify-content-end gap-2">
            <Button variant="outlined" onClick={handleReset}>
              Reset
            </Button>
            <Button
              variant="contained"
              onClick={handleAddRule}
              disabled={emailList.length === 0}
            >
              {editMode ? "Update Alert" : "Create Alert"}
            </Button>
          </div>
        )}
      >

        <div className="px-3">
          <Typography variant="body2">
            Get notified when usage crosses a defined threshold. Alerts are triggered
            once per threshold.
          </Typography>

          <div className="my-3">
            <Typography variant="subtitle1" gutterBottom>
              Usage Threshold
            </Typography>

            <div className="row align-items-end g-3">
              <div className="col">
                <Slider
                  value={Number(threshold)}
                  min={0}
                  max={100}
                  step={5}
                  onChange={(_, value) => setThreshold(String(value))}
                />
              </div>

              <div className="col-auto">
                <ArtTextField
                  label="Threshold %"
                  value={threshold}
                  type="number"
                  onChange={(e: any) => {
                    const value = Math.max(
                      0,
                      Math.min(100, Number(e.target.value) || 0)
                    );
                    setThreshold(String(value));
                  }}
                  marginBottom={0}
                  dataQa="usage-alert-threshold"
                />
              </div>
            </div>

            <Typography variant="body2" className="mt-2">
              You'll be notified once usage crosses this value.
            </Typography>
          </div>

          <Divider />

          {/* Email Recipients */}
          <div className="my-3">
            <Typography variant="subtitle1" gutterBottom>
              Email Recipients
            </Typography>

            <div className="row align-items-end g-2">
              <div className="col">
                <ArtTextField
                  label="Email address"
                  placeholder="Enter email address"
                  value={email}
                  type="email"
                  onChange={(e: any) => setEmail(e.target.value)}
                  marginBottom={0}
                  dataQa="usage-alert-email"
                />
              </div>

              <div className="col-auto">
                <IconButton
                  color="primary"
                  onClick={handleAddEmail}
                >
                  <AddIcon />
                </IconButton>
              </div>
            </div>

            {emailList.length > 0 && (
              <div className="d-flex flex-wrap gap-2 mt-3">
                {emailList.map((emailItem: string) => (
                  <Chip
                    key={emailItem}
                    label={emailItem}
                    onDelete={() => handleRemoveEmail(emailItem)}
                    size="small"
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </ArtDialog>
    </div>
  );
};

export default usePageWrapper(AppUsageAlert, {
  page_title: "Alert",
});
